// Reusable seat-availability math — the single formula used everywhere a
// batch's booked/available seats are shown (BatchSelection, BatchCard, the
// admin Batches page), so the same numbers appear consistently across the
// app.
//
// The actual seat COUNT comes from the batch's own stored "Available Seats"
// field on templateID 3 (batchService.ts's BatchRow.availableSeats) — this
// is the real backend's ground truth and is exactly what the production
// site displays (confirmed live 2026-08-20, screenshot-compared against
// nimasdirang.com for the Paragliding (APGC) batches). An earlier version of
// this service instead recomputed availability by summing Bookings
// (templateID 5) records per batch — that produced numbers that did NOT
// match the live reference site, because the stored counter isn't simply
// "capacity minus valid bookings" (it's whatever the backend/legacy app's
// own process maintains). Trust the stored field; see
// batchService.getLatestAvailableSeats/decrementAvailableSeats for how it's
// read fresh and kept in sync when this app creates a booking.
export interface SeatAvailability {
  totalSeats: number;
  bookedSeats: number;
  availableSeats: number;
  /** 0-100, share of totalSeats that's still available */
  availablePercent: number;
  soldOut: boolean;
}

/** Single formula for seat math everywhere — never lets availableSeats go negative or above capacity, handles missing/null capacity safely. */
export function computeAvailability(capacity: number | null | undefined, bookedSeats: number | null | undefined): SeatAvailability {
  const totalSeats = Number.isFinite(capacity) && (capacity as number) > 0 ? (capacity as number) : 0;
  const bookedRaw = Number.isFinite(bookedSeats) ? (bookedSeats as number) : 0;
  const booked = Math.min(totalSeats, Math.max(0, bookedRaw));
  const availableSeats = Math.max(0, totalSeats - booked);
  const availablePercent = totalSeats > 0 ? Math.round((availableSeats / totalSeats) * 100) : 0;
  return {
    totalSeats,
    bookedSeats: booked,
    availableSeats,
    availablePercent,
    soldOut: totalSeats > 0 && availableSeats === 0,
  };
}

/** capacity - the batch's stored availableSeats, clamped to [0, capacity] — the derived "booked" figure to pass into computeAvailability. */
export function bookedFromAvailable(capacity: number | null | undefined, availableSeatsStored: number | null | undefined): number {
  const totalSeats = Number.isFinite(capacity) && (capacity as number) > 0 ? (capacity as number) : 0;
  const availableRaw = Number.isFinite(availableSeatsStored) ? (availableSeatsStored as number) : totalSeats;
  const available = Math.min(totalSeats, Math.max(0, availableRaw));
  return totalSeats - available;
}
